import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type AccordionKey = 'microEntreprise' | 'entrepriseIndividuelle' | 'eurlSarl' | 'sasuSas';

interface AccordionItem {
  key: AccordionKey;
  num: string;
  title: string;
  subtitle: string;
  content: JSX.Element;
}

export default function DropDownJuridique() {
  const [openKey, setOpenKey] = useState<AccordionKey | null>(null);

  const toggle = (key: AccordionKey) => {
    setOpenKey(prev => (prev === key ? null : key));
  };

  const items: AccordionItem[] = [
    {
      key: 'microEntreprise',
      num: '01',
      title: 'MICRO',
      subtitle: 'Entreprise individuelle au régime fiscal micro',
      content: (
        <>
          <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
            <p>👉 La micro entreprise est une <strong className="text-gray-800">entreprise individuelle</strong> au régime fiscal <strong className="text-gray-800">MICRO</strong>.</p>
            <p>👉 Cette structure ne présente <strong className="text-gray-800">pas de coût de création</strong> de société mais seulement quelques frais d'enregistrement pour déclarer le début d'activité.</p>
            <p>👉 Il n'y a pas de différenciation entre l'entrepreneur personne physique et l'entreprise.</p>
            <p>👉 Le montant des charges sociales est calculé directement avec un <strong className="text-gray-800">taux forfaitaire</strong> (qui varie en fonction de la nature de l'activité) sur le chiffre d'affaires réalisé (CA x taux de cotisations = cotisations sociales dues).</p>
            <p>👉 Le chef d'entreprise est affilié à la <strong className="text-gray-800">sécurité sociale des indépendants</strong>.</p>
            <p>👉 Pour l'impôt sur les revenus, il y a deux possibilités. Soit l'option pour le <strong className="text-gray-800">prélèvement libératoire</strong>, un taux forfaitaire (qui dépend de l'activité réalisée) s'applique au CA. Le paiement est effectué à l'URSSAF en même temps que vos charges sociales. Soit l'option pour le <strong className="text-gray-800">barème progressif</strong>. Vos revenus de la micro entreprise viennent s'ajouter à vos autres revenus du foyer fiscal après abattement (qui varie selon l'activité) sur le CA réalisé.</p>
            <p>👉 La présence d'une comptabilité <strong className="text-gray-800">n'est pas une obligation</strong>. Cela est toutefois conseillé si vous êtes soumis à TVA (si dépassement des seuils de CA).</p>
            <p>👉 Cette forme est idéale pour se lancer ou pour toute activité limitée ayant très peu de charges, notamment les consultants. Idéale également quand vous avez une activité salarié à côté ou que vous ne générez pas beaucoup de CA.</p>
            <p className="flex items-start gap-2 px-4 py-3 rounded-xl" style={{ backgroundColor: 'rgba(69,184,169,0.06)', borderLeft: '3px solid #45B8A9' }}>
              <span>⚠️</span>
              <span>Il faut faire attention aux <strong className="text-gray-800">seuils de chiffre d'affaires</strong> qui sont à respecter pour pouvoir conserver ce statut.</span>
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(69,184,169,0.06)' }}>
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👍 Avantages
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Grande simplicité administrative</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Faible coût de création et de tenue comptable</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Idéale si complément double activité</li>
              </ul>
            </div>
            <div className="rounded-xl p-5 bg-gray-50">
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👎 Inconvénients
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Options vite limitées en termes de gestion</li>
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Pas de déductibilité de charge</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      key: 'entrepriseIndividuelle',
      num: '02',
      title: 'E.I',
      subtitle: 'Entreprise individuelle au régime réel',
      content: (
        <>
          <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
            <p>👉 Il s'agit d'une <strong className="text-gray-800">entreprise individuelle</strong> mais au régime <strong className="text-gray-800">réel d'imposition</strong>.</p>
            <p>👉 Cette structure ne présente <strong className="text-gray-800">pas de coût de création</strong> de société mais seulement quelques frais d'enregistrement pour déclarer le début d'activité.</p>
            <p>👉 Il n'y a pas de différenciation entre l'entrepreneur personne physique et l'entreprise.</p>
            <p>👉 Les cotisations sociales et l'impôt sur les revenus sont, contrairement à la micro entreprise, <strong className="text-gray-800">calculés sur le bénéfice réel</strong> réalisé, basé sur le chiffre d'affaires auquel on déduit toutes les charges liées à l'activité.</p>
            <p>👉 Le chef d'entreprise est affilié à la <strong className="text-gray-800">sécurité sociale des indépendants</strong>.</p>
            <p>👉 Il est <strong className="text-gray-800">obligatoire de tenir une comptabilité</strong> avec un dépôt de liasse fiscale annuelle aux impôts.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(69,184,169,0.06)' }}>
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👍 Avantages
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Faible coût de création</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Toutes les charges sont déductibles</li>
              </ul>
            </div>
            <div className="rounded-xl p-5 bg-gray-50">
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👎 Inconvénients
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Vous n'avez pas de contrôle sur l'imposition</li>
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Les charges sociales peuvent donc facilement être élevées</li>
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> L'impôt sur le revenu peut facilement être élevé</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      key: 'eurlSarl',
      num: '03',
      title: 'EURL/SARL',
      subtitle: 'Société à responsabilité limitée',
      content: (
        <>
          <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
            <p>👉 La forme juridique de l'EURL ou SARL à l'impôt société est dotée d'une <strong className="text-gray-800">personnalité morale</strong>, donc la structure est différenciée de la personne physique de l'entrepreneur. Ce qui assure une sécurité en termes de responsabilité pour le chef d'entreprise.</p>
            <p>👉 Le bénéfice est calculé selon le chiffre d'affaires et les charges réelles. Ce résultat est soumis à l'<strong className="text-gray-800">impôt sur les sociétés</strong>, payé par la société elle-même.</p>
            <p>👉 La rémunération prise par le dirigeant est <strong className="text-gray-800">déductible</strong> du résultat et soumise à charges sociales, elles-même déductibles.</p>
            <p>👉 La rémunération prise est ensuite imposée à l'impôt sur les revenus personnels.</p>
            <p>👉 Le dirigeant est affilié à la <strong className="text-gray-800">sécurité social des indépendants</strong> (si majoritaire au capital de la société).</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(69,184,169,0.06)' }}>
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👍 Avantages
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Possibilité d'optimiser la rémunération</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Faible taux de cotisations sociales (environ 40% de la rémunération nette prise)</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Toutes les charges sont déductibles</li>
              </ul>
            </div>
            <div className="rounded-xl p-5 bg-gray-50">
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👎 Inconvénients
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Coûts de création, de tenue de comptabilité obligatoire</li>
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Couverture sociale limitée, prévoir des complémentaires santé, prévoyance…</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      key: 'sasuSas',
      num: '04',
      title: 'SASU/SAS',
      subtitle: 'Société par actions simplifiée',
      content: (
        <>
          <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
            <p>👉 Le statut juridique de la SASU ou SAS, il s'agit d'une société dotée d'une <strong className="text-gray-800">personnalité morale</strong> et donc différenciée de la personne physique du dirigeant. Ce qui assure une sécurité en termes de responsabilité pour le chef d'entreprise.</p>
            <p>👉 Le bénéfice est calculé selon le chiffre d'affaires et les charges réelles. Ce résultat est soumis à l'<strong className="text-gray-800">impôt sur les sociétés</strong> payé par la société elle-même.</p>
            <p>👉 Les salaires pris par les dirigeants via des bulletins de paies sont soumis à <strong className="text-gray-800">charges sociales</strong> et à <strong className="text-gray-800">impôt sur les revenus</strong>.</p>
            <p>👉 Les dirigeants sont <strong className="text-gray-800">assimilés salariés</strong> et cotisent donc au <strong className="text-gray-800">régime général de la sécurité sociale</strong>.</p>
            <p>👉 Le coût de la rémunération est plus élevé qu'en EURL/SARL mais il est possible de percevoir des dividendes soumis au prélèvement forfaitaire unique (flat tax de 30%) ou au barème progressif sur option.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(69,184,169,0.06)' }}>
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👍 Avantages
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Toutes les charges sont déductibles</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Bonne couverture sociale en cas d'arrêt ou invalidité si salaires cotisés suffisants</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Possibilité d'arbitrer entre salaires et dividendes</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Si exercice d'une activité en parallèle ou si retraité</li>
                <li className="flex items-start gap-2"><span style={{ color: '#45B8A9' }}>●</span> Si bénéficiaire des ARE (France Travail)</li>
              </ul>
            </div>
            <div className="rounded-xl p-5 bg-gray-50">
              <h4 className="text-lg mb-3 flex items-center gap-2" style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}>
                👎 Inconvénients
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Coûts de création, de tenue de la comptabilité obligatoire</li>
                <li className="flex items-start gap-2"><span className="text-gray-400">●</span> Fort taux de cotisations sociales (environ 85% du salaire net)</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-start p-4 pt-32">
      <div className="w-full max-w-6xl mb-16 relative">
        {/* Page Header */}
        <div className="text-center mb-12">
          <span
            className="text-sm tracking-[0.25em] uppercase font-medium"
            style={{ color: '#45B8A9', fontFamily: "'Outfit', sans-serif" }}
          >
            Formes juridiques
          </span>
          <h1
            className="text-4xl md:text-5xl mt-4"
            style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
          >
            Choisissez votre structure juridique
          </h1>
          <div className="w-16 h-0.5 mx-auto mt-6" style={{ backgroundColor: '#45B8A9' }} />
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-12 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-500 p-8 border border-gray-100">
          <div className="flex-1 space-y-6">
            <div className="space-y-4">
              <p className="text-xl text-gray-600" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Vous souhaitez devenir consultant, restaurateur, ou exercer une activité libérale ?
              </p>

              <div className="flex items-center gap-2 text-lg text-gray-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <span className="bg-[#eef7f5] p-2 rounded-full">
                  <svg className="w-6 h-6 text-[#45B8A9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                Découvrez les différentes formes juridiques qui s'offrent à vous
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <span className="px-4 py-2 bg-[#eef7f5] text-[#003942] rounded-full" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>MICRO</span>
                <span className="px-4 py-2 bg-[#eef7f5] text-[#003942] rounded-full" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>E.I</span>
                <span className="px-4 py-2 bg-[#eef7f5] text-[#003942] rounded-full" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>EURL/SARL</span>
                <span className="px-4 py-2 bg-[#eef7f5] text-[#003942] rounded-full" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>SASU/SAS</span>
              </div>
            </div>
          </div>

          <div className="lg:w-1/3">
            <img
              src="/assets/Home.png"
              alt="Illustration juridique"
              className="w-full h-auto rounded-lg shadow-md transition-all duration-300"
            />
          </div>
        </div>
      </div>

      {/* Accordion */}
      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-5 pb-20">
        {items.map((item) => {
          const isOpen = openKey === item.key;
          return (
            <motion.div
              key={item.key}
              layout
              className={`relative rounded-2xl border bg-white overflow-hidden transition-shadow duration-500 ${isOpen ? 'md:col-span-2' : ''}`}
              style={{
                borderColor: isOpen ? 'rgba(69,184,169,0.3)' : 'rgba(229,231,235,1)',
                boxShadow: isOpen
                  ? '0 10px 40px -10px rgba(0,57,66,0.12)'
                  : '0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              {/* Left accent bar */}
              <motion.div
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
                initial={false}
                animate={{
                  backgroundColor: isOpen ? '#45B8A9' : 'transparent',
                }}
                transition={{ duration: 0.3 }}
              />

              {/* Header */}
              <button
                onClick={() => toggle(item.key)}
                className="w-full flex items-center gap-5 px-8 py-6 text-left group"
              >
                {/* Number */}
                <span
                  className="text-3xl shrink-0 transition-colors duration-300"
                  style={{
                    fontFamily: "'Cormorant', serif",
                    fontWeight: 700,
                    color: isOpen ? '#45B8A9' : 'rgba(69,184,169,0.2)',
                  }}
                >
                  {item.num}
                </span>

                {/* Title + subtitle */}
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-2xl"
                    style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm text-gray-400 mt-0.5"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {item.subtitle}
                  </p>
                </div>

                {/* Animated chevron */}
                <motion.div
                  className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                  style={{
                    backgroundColor: isOpen ? 'rgba(69,184,169,0.1)' : 'rgba(0,0,0,0.03)',
                  }}
                  initial={false}
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                >
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={isOpen ? '#45B8A9' : '#9CA3AF'}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </motion.div>
              </button>

              {/* Expandable content */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                      opacity: { duration: 0.3, delay: 0.05 },
                    }}
                    className="overflow-hidden"
                  >
                    <div
                      className="px-8 pb-8 pt-2"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      <div className="w-full h-px bg-gray-100 mb-6" />
                      {item.content}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
